/**
 * Playbook page — the team's concept playbook (Terminology Notes 9/21/26).
 *
 * The Playbook is a collection of every stat's options, customised per team,
 * that Play Entry combines into a playcall one card at a time. Layout:
 *
 *   O/D/ST toggle → stat tabs for that unit (PLAYBOOK_TABS_BY_UNIT) → the
 *   option list with on/off switches, add, edit, reorder, delete/retire.
 *
 * Whose list a tab edits (our team / the opponent) is DERIVED, never chosen:
 * `playbookSide(unit, stat)` returns the side that unit's Play Entry card
 * reads. On Offense the offensive stats are ours and the defensive stats the
 * opponent's; on Defense the reverse; Special Teams are all ours except
 * Opponent Alignment. The header shows it as a static chip.
 *
 * Every option a team authors is visible only to that team's staff and
 * operators (RLS + persona gate + no fanatic route). The unit and stat live in
 * the URL (`unit`, `stat`) so a refresh or a shared link lands on the same
 * tab; a legacy `side` param is ignored and stripped.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useTenantSlug } from '../../app/tenant-slug';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { OdstSegmentedToggle, unitPanelId } from '../../components/odst-segmented-toggle';
import { PageHeader } from '../../components/page-header';
import { Segmented } from '../../components/segmented-control';
import { ApiError } from '../../lib/api-error';
import type { StatOption, StatOptionInput, StatUnit } from '../../lib/api-types';
import { APP_VIEWS, ROUTES } from '../../lib/constants';
import { TableExportButton } from '../integrations/export-dialog';

import { PERSONNEL_CATEGORY, findDefaultPersonnel, hasDefaultPersonnel } from './default-personnel';
import { isDesignTypeCategory } from './design-type';
import { OptionEditor } from './option-editor';
import { OptionRow } from './option-row';
import {
  DEFAULT_OPTION_VIEW,
  SORT_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  applyOptionView,
  canReorder,
  isNarrowed,
  type OptionSort,
  type OptionSourceFilter,
  type OptionStatusFilter,
  type OptionView,
} from './option-view';
import { buildPlaybookExportSource } from './playbook-export';
import {
  PLAYBOOK_TABS_BY_UNIT,
  SIDE_LABELS,
  playbookSide,
  statDefinition,
  type PlaybookUnit,
  type StatCategory,
} from './stat-catalog';

const UNITS: readonly PlaybookUnit[] = ['offense', 'defense', 'special_teams'];

function readUnit(raw: string | null): PlaybookUnit {
  return raw === 'defense' || raw === 'special_teams' ? raw : 'offense';
}

function readStat(raw: string | null, unit: PlaybookUnit): StatCategory {
  const tabs = PLAYBOOK_TABS_BY_UNIT[unit];
  return tabs.includes(raw as StatCategory) ? (raw as StatCategory) : tabs[0]!;
}

type Blocked = { kind: 'forbidden' } | { kind: 'tier'; message: string } | null;

export function PlaybookPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [params, setParams] = useSearchParams();

  const unit = readUnit(params.get('unit'));
  const category = readStat(params.get('stat'), unit);
  const side = playbookSide(unit, category);
  const definition = statDefinition(category);
  const showDesign = isDesignTypeCategory(category);
  const showPersonnel = hasDefaultPersonnel(category);

  const setQuery = useCallback(
    (next: { unit?: PlaybookUnit; stat?: StatCategory }) => {
      setParams(
        (current) => {
          const out = new URLSearchParams(current);
          const nextUnit = next.unit ?? unit;
          out.set('unit', nextUnit);
          const nextStat =
            next.stat ?? (next.unit ? PLAYBOOK_TABS_BY_UNIT[nextUnit][0]! : category);
          out.set('stat', nextStat);
          out.delete('side');
          return out;
        },
        { replace: true }
      );
    },
    [setParams, unit, category]
  );

  // Old links carried `?side=` (the removed Our team / Opponent selector).
  // The side is derived now, so drop the param and keep unit + stat.
  const hasLegacySide = params.has('side');
  useEffect(() => {
    if (!hasLegacySide) return;
    setParams(
      (current) => {
        const out = new URLSearchParams(current);
        out.delete('side');
        return out;
      },
      { replace: true }
    );
  }, [hasLegacySide, setParams]);

  const [items, setItems] = useState<StatOption[] | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [blocked, setBlocked] = useState<Blocked>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  // Formations carry a default personnel group: load the same side's
  // personnel list for the editor's picker and the row badges. Declared
  // before the main load so the stat list stays the page's latest request.
  const [personnel, setPersonnel] = useState<StatOption[] | null | 'error'>(null);
  useEffect(() => {
    if (!showPersonnel) return;
    const controller = new AbortController();
    setPersonnel(null);
    apiClient
      .listStatOptions(PERSONNEL_CATEGORY, side, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setPersonnel(page.items);
      })
      .catch(() => {
        if (!controller.signal.aborted) setPersonnel('error');
      });
    return () => controller.abort();
  }, [apiClient, showPersonnel, side, generation]);
  const personnelList = Array.isArray(personnel) ? personnel : null;

  useEffect(() => {
    const controller = new AbortController();
    setItems(null);
    setLoadError(null);
    apiClient
      .listStatOptions(category, side, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setItems(page.items);
        setCanWrite(page.can_write);
        setBlocked(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setBlocked({ kind: 'forbidden' });
          return;
        }
        if (caught instanceof ApiError && caught.status === 402) {
          setBlocked({ kind: 'tier', message: caught.message });
          return;
        }
        setItems([]);
        setLoadError(
          caught instanceof Error ? caught.message : 'The playbook could not be loaded.'
        );
      });
    return () => controller.abort();
  }, [apiClient, category, side, generation]);

  const [editing, setEditing] = useState<StatOption | 'new' | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StatOption | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [view, setView] = useState<OptionView>(DEFAULT_OPTION_VIEW);
  const patchView = (next: Partial<OptionView>): void =>
    setView((current) => ({ ...current, ...next }));

  useEffect(() => {
    setEditing(null);
    setPendingDelete(null);
    setView(DEFAULT_OPTION_VIEW);
  }, [unit, category, side]);

  const visible = useMemo(
    () => (items === null ? [] : applyOptionView(items, view)),
    [items, view]
  );
  const narrowed = isNarrowed(view);
  const reorderable = canReorder(view);

  const enabledCount = items?.filter((o) => o.is_enabled).length ?? 0;
  const countText =
    items === null
      ? 'Loading…'
      : narrowed
        ? `${visible.length} shown · ${enabledCount} of ${items.length} enabled`
        : `${enabledCount} of ${items.length} enabled`;

  const withToast = async (fn: () => Promise<void>, fallback: string): Promise<void> => {
    try {
      await fn();
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : fallback, 'error');
    }
  };

  const toggle = (option: StatOption): void => {
    setBusyId(option.option_id);
    void withToast(async () => {
      const updated = await apiClient.updateStatOption(option.option_id, {
        is_enabled: !option.is_enabled,
      });
      setItems((current) =>
        current === null
          ? current
          : current.map((o) => (o.option_id === updated.option_id ? updated : o))
      );
    }, 'The option could not be changed.').finally(() => setBusyId(null));
  };

  const setAll = (isEnabled: boolean): void => {
    if (items === null || items.length === 0) return;
    const ids = items.filter((o) => o.is_enabled !== isEnabled).map((o) => o.option_id);
    if (ids.length === 0) return;
    void withToast(async () => {
      await apiClient.setStatOptionsEnabled(category, side, ids, isEnabled);
      setItems((current) =>
        current === null
          ? current
          : current.map((o) => (ids.includes(o.option_id) ? { ...o, is_enabled: isEnabled } : o))
      );
      showToast(
        `${ids.length} option${ids.length === 1 ? '' : 's'} ${isEnabled ? 'enabled' : 'disabled'}.`,
        'success'
      );
    }, 'The options could not be changed.');
  };

  const move = (option: StatOption, delta: -1 | 1): void => {
    if (items === null) return;
    const ordered = [...items].sort((a, b) => a.sort_order - b.sort_order);
    const index = ordered.findIndex((o) => o.option_id === option.option_id);
    const target = index + delta;
    if (index === -1 || target < 0 || target >= ordered.length) return;
    [ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!];
    setItems(ordered.map((o, i) => ({ ...o, sort_order: (i + 1) * 10 })));
    void withToast(async () => {
      const page = await apiClient.reorderStatOptions(
        category,
        side,
        ordered.map((o) => o.option_id)
      );
      setItems(page.items);
    }, 'The order could not be saved.');
  };

  const save = async (input: StatOptionInput, target: StatOption | 'new'): Promise<void> => {
    if (target === 'new') {
      const created = await apiClient.createStatOption(category, side, input);
      setItems((current) => (current === null ? [created] : [...current, created]));
      showToast(
        `${created.label} added to ${SIDE_LABELS[side].toLowerCase()} ${definition.label}.`,
        'success'
      );
    } else {
      const updated = await apiClient.updateStatOption(target.option_id, {
        label: input.label,
        notes: input.notes ?? null,
        ...(input.design_type !== undefined ? { design_type: input.design_type } : {}),
        ...(input.personnel_group_id !== undefined
          ? { personnel_group_id: input.personnel_group_id }
          : {}),
      });
      setItems((current) =>
        current === null
          ? current
          : current.map((o) => (o.option_id === updated.option_id ? updated : o))
      );
      showToast(`${updated.label} saved.`, 'success');
    }
    setEditing(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (pendingDelete === null) return;
    const target = pendingDelete;
    setBusyId(target.option_id);
    await withToast(async () => {
      const result = await apiClient.removeStatOption(target.option_id);
      if (result === null) {
        setItems((current) =>
          current === null ? current : current.filter((o) => o.option_id !== target.option_id)
        );
        showToast(`${target.label} deleted.`, 'success');
      } else {
        setItems((current) =>
          current === null || result.option === null
            ? current
            : current.map((o) => (o.option_id === target.option_id ? result.option! : o))
        );
        showToast(`${target.label} disabled — it stays on past plays.`, 'info');
      }
      setPendingDelete(null);
    }, 'The option could not be removed.');
    setBusyId(null);
  };

  if (blocked?.kind === 'forbidden') {
    return (
      <BlockedPanel
        title="Playbook is not available for your role"
        message="The playbook is visible to your coaching staff and stat operators. Ask your team admin if you need access."
        isTierBlocked={false}
      />
    );
  }
  if (blocked?.kind === 'tier') {
    return (
      <BlockedPanel
        title="Upgrade to use the Playbook"
        message={blocked.message || 'The Playbook is available on the Starter plan and above.'}
        isTierBlocked
      />
    );
  }

  const tabs = PLAYBOOK_TABS_BY_UNIT[unit];

  return (
    <section className="stack playbook" data-unit={unit} data-stat={category} data-side={side}>
      <PageHeader
        className="playbook__header"
        title="Playbook"
        context={
          <>
            <span className="playbook__context-count">
              {definition.label} ·{' '}
              <span aria-live="polite" data-testid="playbook-count">
                {countText}
              </span>
            </span>
            <span className="playbook__intro">
              Only enabled options appear on Play Entry.{' '}
              <Link to={`${ROUTES.docs}/terminology`}>Terminology</Link>
            </span>
          </>
        }
        actions={
          <>
            <OdstSegmentedToggle
              value={unit as StatUnit}
              onChange={(next) => setQuery({ unit: next as PlaybookUnit })}
              units={UNITS as readonly StatUnit[]}
              scope="playbook"
            />
            <TableExportButton source={() => buildPlaybookExportSource(apiClient, unit)} />
          </>
        }
      />

      <div
        className="playbook__body"
        id={unitPanelId(unit as StatUnit, 'playbook')}
        role="tabpanel"
      >
        <nav className="playbook__tabs" aria-label={`${unit.replace('_', ' ')} stats`}>
          <Segmented
            label="Stat"
            className="playbook__stat-tabs"
            testId="playbook-stat-tabs"
            options={tabs.map((tab) => {
              const d = statDefinition(tab);
              // Short labels stay unique on a phone: the OTHER unit's stats
              // carry an "Opp." prefix ("Concept" vs "Opp. Concept").
              const isOtherUnit = d.unit !== unit;
              return {
                value: tab,
                label: d.label,
                shortLabel: isOtherUnit ? `Opp. ${d.short_label}` : d.short_label,
              };
            })}
            value={category}
            onChange={(next) => setQuery({ stat: next })}
          />
        </nav>

        <section className="playbook__stat card" aria-labelledby="playbook-stat-title">
          <header className="playbook__stat-head">
            <div className="playbook__title-row">
              <h2 id="playbook-stat-title">{definition.label}</h2>
              <span
                className={`playbook__side playbook__side--${side}`}
                data-testid="playbook-side"
                title={
                  side === 'own'
                    ? 'Options your team calls'
                    : 'Options you expect the opponent to show'
                }
              >
                {SIDE_LABELS[side]}
              </span>
            </div>
            <p className="muted playbook__definition">{definition.definition}</p>
          </header>

          <div className="playbook__toolbar">
            <label className="playbook__search">
              <span className="visually-hidden">Search options</span>
              <input
                type="search"
                className="field__control"
                placeholder="Search options…"
                value={view.search}
                onChange={(e) => patchView({ search: e.target.value })}
                data-testid="playbook-search"
              />
            </label>
            <div className="playbook__view-controls">
              <ViewSelect<OptionSort>
                label="Sort"
                testId="playbook-sort"
                value={view.sort}
                labels={SORT_LABELS}
                onChange={(sort) => patchView({ sort })}
              />
              <ViewSelect<OptionStatusFilter>
                label="Status"
                testId="playbook-filter-status"
                value={view.status}
                labels={STATUS_LABELS}
                onChange={(status) => patchView({ status })}
              />
              <ViewSelect<OptionSourceFilter>
                label="Source"
                testId="playbook-filter-source"
                value={view.source}
                labels={SOURCE_LABELS}
                onChange={(source) => patchView({ source })}
              />
            </div>
            {canWrite ? (
              <div className="playbook__toolbar-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setAll(true)}
                  disabled={items === null}
                >
                  Enable all
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setAll(false)}
                  disabled={items === null}
                >
                  Disable all
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  data-testid="playbook-add"
                  onClick={() => setEditing('new')}
                >
                  Add option
                </button>
              </div>
            ) : null}
          </div>

          {loadError !== null ? (
            <p className="banner banner--error" role="alert">
              {loadError}{' '}
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setGeneration((g) => g + 1)}
              >
                Retry
              </button>
            </p>
          ) : null}

          {items !== null && items.length === 0 && loadError === null ? (
            <p className="playbook__empty muted" data-testid="playbook-empty">
              No options yet. {canWrite ? 'Add the first one.' : 'Ask a coach to add some.'}
            </p>
          ) : null}

          {items !== null && items.length > 0 && visible.length === 0 ? (
            <p className="playbook__empty muted" data-testid="playbook-no-match">
              {view.status === 'all' && view.source === 'all'
                ? `Nothing matches “${view.search.trim()}”.`
                : 'No options match these filters.'}{' '}
              <button
                type="button"
                className="btn btn--ghost"
                data-testid="playbook-clear-filters"
                onClick={() => patchView({ search: '', status: 'all', source: 'all' })}
              >
                Clear filters
              </button>
            </p>
          ) : null}

          <ul className="playbook__options" data-testid="playbook-options">
            {visible.map((option, index) => (
              <OptionRow
                key={option.option_id}
                option={option}
                canWrite={canWrite}
                isBusy={busyId === option.option_id}
                isFirst={index === 0}
                isLast={index === visible.length - 1}
                canReorder={reorderable}
                showGlyph={category === 'FORMATION'}
                showDesign={showDesign}
                defaultPersonnel={
                  showPersonnel ? findDefaultPersonnel(option, personnelList) : undefined
                }
                onToggle={() => toggle(option)}
                onEdit={() => setEditing(option)}
                onDelete={() => setPendingDelete(option)}
                onMove={(delta) => move(option, delta)}
              />
            ))}
          </ul>
        </section>
      </div>

      {editing !== null ? (
        <OptionEditor
          title={
            editing === 'new'
              ? `Add ${SIDE_LABELS[side].toLowerCase()} ${definition.label}`
              : `Edit ${editing.label}`
          }
          initial={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={(input) => save(input, editing)}
          showDesign={showDesign}
          personnelOptions={showPersonnel ? personnel : undefined}
        />
      ) : null}

      {pendingDelete !== null ? (
        <ConfirmDialog
          title={
            pendingDelete.is_stock
              ? `Disable ${pendingDelete.label}?`
              : `Delete ${pendingDelete.label}?`
          }
          consequence={
            pendingDelete.is_stock
              ? 'Stock options are never deleted — this turns it off so it stops appearing on Play Entry. Past plays keep the value.'
              : pendingDelete.usage_count > 0
                ? `${pendingDelete.usage_count} play${pendingDelete.usage_count === 1 ? '' : 's'} already use this option, so it will be disabled rather than deleted.`
                : 'This option has never been used on a play and will be removed.'
          }
          confirmLabel={
            pendingDelete.is_stock || pendingDelete.usage_count > 0 ? 'Disable' : 'Delete'
          }
          tone="danger"
          isConfirmDisabled={busyId === pendingDelete.option_id}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </section>
  );
}

function ViewSelect<T extends string>({
  label,
  testId,
  value,
  labels,
  onChange,
}: {
  label: string;
  testId: string;
  value: T;
  labels: Readonly<Record<T, string>>;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <label className="playbook__view-select">
      <span className="playbook__view-select-label muted">{label}</span>
      <select
        className="field__control"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {(Object.keys(labels) as T[]).map((key) => (
          <option key={key} value={key}>
            {labels[key]}
          </option>
        ))}
      </select>
    </label>
  );
}

export function BlockedPanel({
  title,
  message,
  isTierBlocked,
}: {
  title: string;
  message: string;
  isTierBlocked: boolean;
}): JSX.Element {
  const { persona } = useSession();
  const tenantSlug = useTenantSlug();
  // Billing is Team Admin only; everyone else is told who can upgrade.
  const canUpgrade =
    (persona === 'team_admin' || persona === 'platform_admin') && tenantSlug !== null;
  return (
    <div className="card playbook-blocked" role="alert">
      <h2 className="playbook-blocked__title">{title}</h2>
      <p>{message}</p>
      {isTierBlocked ? (
        canUpgrade ? (
          <Link
            className="btn btn--primary"
            to={`/app/${tenantSlug}/${APP_VIEWS.billing}?tab=plan`}
          >
            See plans &amp; upgrade
          </Link>
        ) : (
          <p className="muted">Ask your Team Admin to upgrade the team&apos;s plan.</p>
        )
      ) : null}
    </div>
  );
}
